import {
  CalculationNoticeType,
  CalculationStatus,
  EmissionCalculation,
  EmissionScope,
  Scope2Method,
} from '../../types/emissions';

export const SCOPE_LABELS: Record<EmissionScope, string> = {
  [EmissionScope.Scope1]: 'Scope 1',
  [EmissionScope.Scope2]: 'Scope 2',
  [EmissionScope.Scope3]: 'Scope 3',
};

export const SCOPE2_METHOD_LABELS: Record<Scope2Method, string> = {
  [Scope2Method.LocationBased]: 'Location-Based',
  [Scope2Method.MarketBased]: 'Market-Based',
};

export const STATUS_CONFIG: Record<CalculationStatus, { className: string; label: string }> = {
  [CalculationStatus.Calculated]: {
    className: 'tw:bg-emerald-500/10 tw:text-emerald-700 tw:border-emerald-200',
    label: 'Calculated',
  },
  [CalculationStatus.Pending]: {
    className: 'tw:bg-amber-500/10 tw:text-amber-700 tw:border-amber-200',
    label: 'Pending',
  },
  [CalculationStatus.Failed]: {
    className: 'tw:bg-red-500/10 tw:text-red-700 tw:border-red-200',
    label: 'Failed',
  },
  [CalculationStatus.Error]: {
    className: 'tw:bg-red-500/10 tw:text-red-700 tw:border-red-200',
    label: 'Error',
  },
  [CalculationStatus.RequiresReview]: {
    className: 'tw:bg-amber-500/10 tw:text-amber-700 tw:border-amber-200',
    label: 'Requires Review',
  },
};

export const NOTICE_COLORS: Record<CalculationNoticeType, string> = {
  [CalculationNoticeType.Info]: 'info',
  [CalculationNoticeType.Warning]: 'warning',
  [CalculationNoticeType.Error]: 'danger',
};

export const NOTICE_BADGE_CLASSES: Record<CalculationNoticeType, string> = {
  [CalculationNoticeType.Info]: 'tw:bg-blue-500/10 tw:text-blue-700 tw:border-blue-200',
  [CalculationNoticeType.Warning]: 'tw:bg-amber-500/10 tw:text-amber-700 tw:border-amber-200',
  [CalculationNoticeType.Error]: 'tw:bg-red-500/10 tw:text-red-700 tw:border-red-200',
};

export const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatDateShort = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const formatFactorValue = (value: number): string => {
  if (value === 0) {
    return '0';
  }
  if (!Number.isFinite(value)) {
    return String(value);
  }
  // Pick decimal count that keeps at least 3 significant figures
  const magnitude = Math.floor(Math.log10(Math.abs(value)));
  const decimals = Math.max(2, 2 - magnitude + 2);
  return value.toFixed(Math.min(decimals, 10));
};

const SOURCE_LABELS: Record<string, string> = {
  defra: 'DEFRA',
  iea: 'IEA',
  epa: 'EPA',
  'climatiq energy api': 'Energy API',
  'climatiq heat api': 'Heat API',
  climatiq: 'Factors API',
  certificate: 'Certificate',
  direct_contract: 'Direct Contract',
  supplier_specific: 'Supplier Specific',
  residual_mix: 'Residual Mix',
};

export const formatSource = (source: string): string => {
  return SOURCE_LABELS[source.toLowerCase()] ?? source.toUpperCase();
};

const PROVIDER_LABELS: Record<string, string> = {
  climatiq: 'Emissions',
  service: 'API',
  internal: 'Internal',
  manual: 'Manual',
};

export const formatProvider = (provider: string): string => {
  return PROVIDER_LABELS[provider.toLowerCase()] ?? provider;
};

export const formatCO2e = (
  value: number | undefined,
  showInTonnes: boolean,
  decimalPlaces: number,
): string => {
  if (value === undefined || value === null) {
    return '--';
  }
  if (showInTonnes) {
    return `${(value / 1000).toLocaleString('en-US', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    })} t CO₂e`;
  }
  return `${value.toLocaleString('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  })} kg CO₂e`;
};

/**
 * Same as `formatCO2e` but auto-bumps decimal precision for a single
 * row when the value is too small to render at the configured
 * precision. Used by the per-gas breakdown card where CH₄ / N₂O
 * contributions are often 1–4 orders of magnitude smaller than CO₂ —
 * at `tonnes` + 2dp those lines would otherwise read `0.00 t CO₂e`.
 *
 * The unit (t or kg) is preserved — only the decimal count grows,
 * just enough to show ~3 significant figures, capped at 8 to keep
 * line widths sane.
 */
export const formatCO2eAutoUnit = (
  value: number | undefined,
  showInTonnes: boolean,
  decimalPlaces: number,
): string => {
  if (value === undefined || value === null) {
    return '--';
  }
  if (value === 0) {
    return formatCO2e(value, showInTonnes, decimalPlaces);
  }
  // Project the value into the display unit (kg or tonnes) and find the
  // smallest decimal count that gives ~3 significant figures. If the
  // user's chosen precision already covers it, keep their precision.
  const displayValue = showInTonnes ? Math.abs(value) / 1000 : Math.abs(value);
  const magnitude = Math.floor(Math.log10(displayValue));
  // ~3 significant figures: e.g. 0.0023 needs 4dp (10^-3 magnitude → 4).
  const requiredDecimals = Math.max(0, 2 - magnitude);
  const decimals = Math.min(8, Math.max(decimalPlaces, requiredDecimals));
  return formatCO2e(value, showInTonnes, decimals);
};

export const hasDetails = (calculation: EmissionCalculation): boolean =>
  Boolean(
    calculation.emissionFactorId ||
      calculation.climatiqFactorId ||
      calculation.climatiqActivityId ||
      calculation.emissionFactorSourceLink ||
      (calculation.sourceTrail && calculation.sourceTrail.length > 0),
  );
